import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { isMobile } from 'react-device-detect';

import cars from './../data/cars.json';
import carsGalleries from './../data/carGalleries.json';
import languages from './../data/languages.json';
import localization from './../data/localization.json';
import showroom from './../data/showroom.json';

import {
  getCarInfo,
  getLanguageInfo,
  createPrefixOfUrl,
  gtmNavigationInteraction,
  gtmPageView,
  gtmNavigationAction,
  gtmJourneyEvent,
  gtmShowroomGalleryInteraction,
} from './../common';
import { getFontClassName } from './../styleFunctions';

import { LoadingScreen } from './components/loading';
import { PageWrapper } from './components/pageWrapper';
import { CustomModal, TransparentFullScreenModal } from './components/modal';
import { FullHeightDarkMenuWrapper } from './components/menus';
import { InfoTextDiv, NavigationBar, FullScreenImageCarousel } from './components/others';
import { ImageComp } from './components/image';
import IframeComp from './components/iframe';
import { useResponsiveSize } from './components/hooks';
import { useWindowHeight, useWindowWidth } from '@react-hook/window-size';

const PC_MODEL_URL = '/static/showroom/pc.html';
const MOBILE_MODEL_URL = '/static/showroom/mobile.html';

const carOrder = [];

const FirstShowRoomModal = ({ opened = false, language = 'es', closeFunc = () => {} }) => {
  const getResponsiveSize = useResponsiveSize();
  const windowWidth = useWindowWidth();
  const windowHeight = useWindowHeight();
  const titleMarginBottom = useMemo(() => {
    if (windowWidth / windowHeight <= 0.46) {
      return 80;
    } else {
      return Math.round(Math.max(((60 * 0.46) / windowWidth) * windowHeight, 12));
    }
  }, [windowWidth, windowHeight]);
  return (
    <TransparentFullScreenModal opened={opened}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          width: '100%',
        }}
      >
        <div
          className={getFontClassName('bold', language)}
          style={{
            fontSize: getResponsiveSize(36, windowWidth > windowHeight ? 26.5 : 34),
            color: '#fff',
            textAlign: 'center',
            borderBottom: '1px solid #fff',
            paddingBottom: getResponsiveSize(30, windowWidth > windowHeight ? 20 : 28),
            paddingTop: 0,
            paddingLeft: getResponsiveSize(20, 20),
            paddingRight: getResponsiveSize(20, 20),
            maxWidth: getResponsiveSize(577, windowWidth > windowHeight ? 440 : 338),
            marginTop: getResponsiveSize(32, windowWidth > windowHeight ? 30 : 74),
            marginBottom: getResponsiveSize(68, windowWidth > windowHeight ? 16 : titleMarginBottom),
          }}
          dangerouslySetInnerHTML={{
            __html:
              localization[isMobile ? 'first_showroom_modal_title_mobile' : 'first_showroom_modal_title'][language],
          }}
        />
        <div
          style={
            isMobile
              ? {
                  flex: 1,
                  maxWidth:
                    windowWidth > windowHeight
                      ? getResponsiveSize(340, 340)
                      : `calc(100% - ${getResponsiveSize(100, 100)}px)`,
                  overflowX: 'hidden',
                  overflowY: 'auto',
                }
              : { maxWidth: getResponsiveSize(520, 520), overflowX: 'hidden', overflowY: 'auto' }
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', width: '100%' }}>
            <div style={{ display: 'flex' }}>
              <div
                style={{
                  width: getResponsiveSize(140, 140),
                  border: '1px solid #fff',
                  borderBottom: 'none',
                  textAlign: 'center',
                  padding: `${getResponsiveSize(26, windowWidth > windowHeight ? 20 : 26)}px ${getResponsiveSize(20, windowWidth > windowHeight ? 10 : 20)}px`,
                }}
              >
                <div
                  className={getFontClassName('medium', language)}
                  style={{
                    fontSize: getResponsiveSize(22, windowWidth > windowHeight ? 14 : 22),
                    marginBottom: getResponsiveSize(20, 20),
                  }}
                >
                  {localization['first_showroom_modal_txt_1'][language]}
                </div>
                <ImageComp
                  src={'/static/UI/guide-spot-gallery.png'}
                  style={{
                    width: getResponsiveSize(77, windowWidth > windowHeight ? 60 : 77),
                    height: getResponsiveSize(100, windowWidth > windowHeight ? 78 : 100),
                  }}
                  alt="exhibition zone icon"
                />
              </div>
              <div
                className={getFontClassName('light', language)}
                style={{
                  flex: 1,
                  paddingLeft: getResponsiveSize(24, 24),
                  paddingTop: getResponsiveSize(26, windowWidth > windowHeight ? 20 : 26) + 1,
                  fontSize: getResponsiveSize(24, windowWidth > windowHeight ? 18 : 23),
                }}
              >
                {localization['first_showroom_modal_txt_2'][language]}
              </div>
            </div>
            <div style={{ display: 'flex' }}>
              <div
                style={{
                  width: getResponsiveSize(140, 140),
                  border: '1px solid #fff',
                  borderTop: 'none',
                  textAlign: 'center',
                  padding: `0 ${getResponsiveSize(20, windowWidth > windowHeight ? 10 : 20)}px`,
                }}
              >
                <div
                  className={getFontClassName('medium', language)}
                  style={{
                    borderTop: '1px solid #fff',
                    fontSize: getResponsiveSize(22, windowWidth > windowHeight ? 14 : 22),
                    paddingTop: getResponsiveSize(26, windowWidth > windowHeight ? 20 : 26),
                    marginBottom: getResponsiveSize(20, 20),
                  }}
                >
                  {localization['first_showroom_modal_txt_3'][language]}
                </div>
                <ImageComp
                  src={'/static/UI/guide-spot-car.png'}
                  style={{
                    width: getResponsiveSize(75, windowWidth > windowHeight ? 59 : 75),
                    height: getResponsiveSize(104, windowWidth > windowHeight ? 81.5 : 104),
                    marginBottom: getResponsiveSize(26, 26),
                  }}
                  alt="customer zone icon"
                />
              </div>
              <div
                className={getFontClassName('light', language)}
                style={{
                  flex: 1,
                  paddingLeft: getResponsiveSize(24, 24),
                  paddingTop: getResponsiveSize(26, windowWidth > windowHeight ? 20 : 26) + 1,
                  fontSize: getResponsiveSize(24, windowWidth > windowHeight ? 18 : 23),
                }}
              >
                {localization['first_showroom_modal_txt_4'][language]}
              </div>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex' }}>
          <button
            className={getFontClassName('bold', language)}
            style={{
              background: '#fff',
              border: 'none',
              width: getResponsiveSize(230, windowWidth > windowHeight ? 180 : 230),
              height: getResponsiveSize(77, windowWidth > windowHeight ? 60 : 77),
              borderRadius: getResponsiveSize(40, 40),
              fontSize: getResponsiveSize(26, windowWidth > windowHeight ? 20 : 26),
              cursor: 'pointer',
              marginTop: getResponsiveSize(102, windowWidth > windowHeight ? 16 : 20),
              marginBottom: getResponsiveSize(32, windowWidth > windowHeight ? 30 : 115),
            }}
            onClick={() => closeFunc()}
          >
            {localization['first_showroom_modal_button'][language]}
          </button>
        </div>
      </div>
    </TransparentFullScreenModal>
  );
};

const ShowRoomMenu = ({ opened, closeFunc, languageInfo, setCarId, setWillCarShowModalOpen, lastCar }) => {
  const getResponsiveSize = useResponsiveSize();
  return (
    <FullHeightDarkMenuWrapper
      opened={opened}
      closeFunc={closeFunc}
      width={getResponsiveSize(540, 540)}
      languageInfo={languageInfo}
    >
      {cars.map((ele, key) => (
        <button
          key={`car-button-${key}`}
          className={getFontClassName('bold', languageInfo.id)}
          style={{
            color: '#fff',
            fontSize: getResponsiveSize(32, 32),
            width: '100%',
            padding: `${getResponsiveSize(26, 26)}px 0`,
            background: 'transparent',
            cursor: 'pointer',
            border: 'none',
            borderBottom: cars.length === key + 1 ? '2px solid white' : '1px solid slategray',
            display: 'flex',
            alignItems: 'center',
          }}
          onClick={() => {
            setCarId(ele.id);
            setWillCarShowModalOpen(true);
            gtmNavigationAction('model menu', ele.brand);
            gtmJourneyEvent('model selection', 'select vehicle', ele.brand);
          }}
        >
          <div style={{ flex: 1 }} dangerouslySetInnerHTML={{ __html: ele.brandHTML }}></div>
          <ImageComp
            src={'/static/UI/btn-list-arrow@2x.png'}
            style={{
              width: getResponsiveSize(11, 11),
              height: getResponsiveSize(19, 19),
              marginRight: getResponsiveSize(20, 20),
            }}
          />
        </button>
      ))}
      <button
        className={getFontClassName('light', languageInfo.id)}
        style={{
          color: '#fff',
          fontSize: getResponsiveSize(24, 23),
          width: '100%',
          paddingTop: getResponsiveSize(40, 40),
          paddingBottom: getResponsiveSize(40, 40),
          background: 'transparent',
          cursor: 'pointer',
          border: 'none',
        }}
        onClick={() => {
          gtmNavigationAction('tab menu', `external|${localization['menu_txt_1'][languageInfo.id]}`);
          window.open(showroom.links.test_drive, '_blank');
        }}
      >
        {localization['menu_txt_1'][languageInfo.id]}
      </button>
      {showroom.links.request_catalogue && (
        <button
          className={getFontClassName('light', languageInfo.id)}
          style={{
            color: '#fff',
            fontSize: getResponsiveSize(24, 23),
            width: '100%',
            paddingBottom: getResponsiveSize(40, 40),
            background: 'transparent',
            cursor: 'pointer',
            border: 'none',
          }}
          onClick={() => {
            gtmNavigationAction('tab menu', `external|${localization['menu_txt_4'][languageInfo.id]}`);
            window.open(showroom.links.request_catalogue, '_blank');
          }}
        >
          {localization['menu_txt_4'][languageInfo.id]}
        </button>
      )}
      <button
        className={getFontClassName('light', languageInfo.id)}
        style={{
          color: '#fff',
          fontSize: getResponsiveSize(24, 23),
          width: '100%',
          paddingBottom: getResponsiveSize(40, 40),
          background: 'transparent',
          cursor: 'pointer',
          border: 'none',
        }}
        onClick={() => {
          gtmNavigationAction('tab menu', `external|${localization['menu_txt_2'][languageInfo.id]}`);
          window.open(showroom.links.find_dealer, '_blank');
        }}
      >
        {localization['menu_txt_2'][languageInfo.id]}
      </button>
      {showroom.links.quotation && (
        <button
          className={getFontClassName('light', languageInfo.id)}
          style={{
            color: '#fff',
            fontSize: getResponsiveSize(24, 23),
            width: '100%',
            paddingBottom: getResponsiveSize(40, 40),
            background: 'transparent',
            cursor: 'pointer',
            border: 'none',
          }}
          onClick={() => {
            gtmNavigationAction('tab menu', `external|${localization['menu_txt_3'][languageInfo.id]}`);
            window.open(showroom.links.quotation, '_blank');
          }}
        >
          {localization['menu_txt_3'][languageInfo.id]}
        </button>
      )}
    </FullHeightDarkMenuWrapper>
  );
};

const GalleryMenu = ({ opened, closeFunc, languageInfo, onCarClick = () => {} }) => {
  const getResponsiveSize = useResponsiveSize();
  return (
    <FullHeightDarkMenuWrapper
      opened={opened}
      closeFunc={closeFunc}
      languageInfo={languageInfo}
      width={getResponsiveSize(540, 540)}
    >
      <div
        className={getFontClassName('bold', languageInfo.id)}
        style={{
          color: 'white',
          fontSize: getResponsiveSize(32, 32),
          textAlign: 'center',
          padding: `${getResponsiveSize(21, 21)}px 0`,
          borderBottom: '2px solid #fff',
        }}
      >
        {localization['gallery'][languageInfo.id]}
      </div>
      {carsGalleries.map((ele, key) => {
        return (
          <div
            key={key}
            style={{ cursor: 'pointer' }}
            onClick={() => {
              onCarClick(ele.id);
            }}
          >
            <div
              style={{
                textAlign: 'center',
                paddingTop: getResponsiveSize(30, 30),
                paddingBottom: getResponsiveSize(14, 14),
              }}
            >
              <ImageComp
                src={ele.carSrc}
                style={{
                  width: getResponsiveSize(310, 310),
                  height: getResponsiveSize(120, 120),
                  objectFit: 'contain',
                }}
              />
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingBottom: getResponsiveSize(12, 12),
                borderBottom: '1px solid #4d5860',
              }}
            >
              <div
                dangerouslySetInnerHTML={{ __html: ele.brandHTML }}
                className={getFontClassName('bold', languageInfo.id)}
                style={{ color: 'white', fontSize: getResponsiveSize(23, 23) }}
              />
              <ImageComp
                src={'/static/UI/btn-list-arrow.png'}
                style={{ width: getResponsiveSize(12, 12), height: getResponsiveSize(21, 21), objectFit: 'contain' }}
              />
            </div>
          </div>
        );
      })}
    </FullHeightDarkMenuWrapper>
  );
};

const ShowRoom = () => {
  const history = useHistory();
  const { state } = useLocation();

  const language = useSelector(({ languageReducer }) => languageReducer.language);
  const autoComplete = useSelector(({ loadingStateReducer }) => loadingStateReducer.autoComplete);
  const notificationShow = useSelector(({ notificationReducer }) => notificationReducer.showroom);
  const dispatch = useDispatch();
  const getResponsiveSize = useResponsiveSize();
  const [firstModalOpen, setFirstModalOpen] = useState(true);
  const [fullMenuOpened, setOpenFullMenu] = useState(false);
  const [galleryMenuOpened, setGalleryMenuOpened] = useState(false);
  const [carGalleryOpened, setCarGalleryOpened] = useState(false);
  const [willCarShowModalOpen, setWillCarShowModalOpen] = useState(false);
  const [carId, setCarId] = useState(null);
  const [carColor, setCarColor] = useState(null);

  // loading screen
  const [loadingEnded, setLoadingEnded] = useState(false);

  // iframe
  const [willLoadIframe, setWillLoadIframe] = useState(false);
  const [isSceneLoading, setIsSceneLoading] = useState(false);
  const iframeRef = useRef(null);

  const getExOrInModalWidth = () => getResponsiveSize(420, 420);

  const startShowroom = useCallback(() => {
    if (iframeRef !== null) {
      if (isSceneLoading) return;
      setIsSceneLoading(true);
      iframeRef.current.contentWindow.postMessage(JSON.stringify({ message: 'showroom' }), '*');
    }
  }, [iframeRef, isSceneLoading, setIsSceneLoading]);

  const handleLoad = useCallback(
    (event) => {
      if (iframeRef === null) return;

      const data = event.data;
      try {
        const obj = JSON.parse(data);
        switch (obj.message) {
          case 'spotClicked':
            // setSpot(obj.value);
            // console.log(obj)
            const postData = {
              message: 'spot',
              payload: obj.payload,
            };
            iframeRef.current.contentWindow.postMessage(JSON.stringify(postData), '*');
            setCarId(cars[Number(obj.payload.car)].id);
            // obj.payload.color
            setCarColor(Number(obj.payload.color));
            setWillCarShowModalOpen(true);
            gtmNavigationInteraction('cta click', 'explore more', cars[Number(obj.payload.car)].brand);
            break;

          case 'scriptLoaded':
            /* script is loaded. now we can load engine */
            iframeRef.current.contentWindow.postMessage(JSON.stringify({ message: 'loadEngine' }), '*');
            break;

          case 'engineLoaded':
            /* engine is loaded. now we can load a scene(exterior / interior) */
            // setEngineLoaded(true)

            /* load exterior / interior */
            setTimeout(() => {
              startShowroom();
            }, 1000);
            break;

          case 'sceneLoaded':
            /* scene is loaded. now we can control everything */
            window.localStorage.setItem('sceneLoaded', 'yes');
            setTimeout(() => {
              setTimeout(() => {
                /* FOR EXTERIOR ONLY */
                setIsSceneLoading(false);
              }, 500);
            }, 200);
            break;

          case 'gallerySpotClicked':
            setGalleryMenuOpened(true);
            gtmNavigationAction('cta click', 'gallery');
            break;
          case 'toGallerySpotClicked':
            gtmNavigationAction('cta click', 'customer zone');
            break;
          case 'toCarSpotClicked':
            gtmNavigationAction('cta click', 'exhibition zone');
            break;
          default:
            break;
        }
      } catch (err) {}
    },
    [iframeRef, setCarId, setCarColor, setWillCarShowModalOpen, setIsSceneLoading, setGalleryMenuOpened, startShowroom],
  );

  useEffect(() => {
    if (!willLoadIframe) {
      window.localStorage.setItem('engineLoadProgress', 0);
      window.localStorage.setItem('sceneLoaded', 'no');
      window.onmessage = handleLoad;
    }
  }, [willLoadIframe, handleLoad]);

  useEffect(() => {
    gtmPageView('showroom');
  }, []);

  return (
    <>
      {!loadingEnded ? (
        <LoadingScreen
          cars={carOrder.map((carId) => getCarInfo(carId, true))}
          realLoad
          autoComplete={autoComplete}
          middleFunction={() => setWillLoadIframe(true)}
          completeFunction={(lang_id = null) => {
            if (lang_id !== null) {
              dispatch({ type: 'LANGUAGE_CHANGED', language: lang_id });
            }
            setTimeout(() => {
              setLoadingEnded(true);
              dispatch({ type: 'CHANGE_AUTO_COMPLETED', value: true });
              if (languages.length > 1 && lang_id !== null) {
                gtmNavigationAction('cta click', getLanguageInfo(lang_id).title);
              } else {
                gtmNavigationAction('cta click', 'start');
              }
            }, 1000);
          }}
        />
      ) : null}
      {willLoadIframe ? (
        <PageWrapper carId={'default'}>
          <IframeComp
            ref={iframeRef}
            src={isMobile ? MOBILE_MODEL_URL : PC_MODEL_URL}
            style={{ height: '100%', width: '100%', border: 'none', outline: 'none', padding: 0, margin: 0 }}
            title="showroom-iframe"
          />
          <NavigationBar
            onMenuClick={() => {
              setOpenFullMenu(true);
              gtmNavigationAction('sticky menu', 'menu');
            }}
            language={language}
          >
            <div
              style={
                language !== null && getLanguageInfo(language).rtl
                  ? {
                      position: 'fixed',
                      left: getResponsiveSize(141, 118),
                      top: getResponsiveSize(21, 23),
                      width: getResponsiveSize(38, 37),
                      height: getResponsiveSize(39, 39),
                      zIndex: 5,
                    }
                  : {
                      position: 'fixed',
                      right: getResponsiveSize(141, 118),
                      top: getResponsiveSize(21, 23),
                      width: getResponsiveSize(38, 37),
                      height: getResponsiveSize(39, 39),
                      zIndex: 5,
                    }
              }
            >
              <ImageComp
                src={'/static/UI/btn-bookonline.png'}
                style={{ width: '100%', height: '100%', cursor: 'pointer' }}
                alt="book online"
                onClick={() => {
                  window.open(showroom.links.book_online, '_blank');
                  gtmNavigationAction('sticky menu', 'book online');
                }}
              />
            </div>
          </NavigationBar>
          {loadingEnded ? <InfoTextDiv color="#ffffff" /> : null}
          <FirstShowRoomModal
            opened={notificationShow && firstModalOpen}
            closeFunc={() => {
              setFirstModalOpen(false);
              dispatch({ type: 'CHANGE_SHOWROOM_NOTIFICATION', value: false });
            }}
            language={language}
          />
          <CustomModal
            opened={willCarShowModalOpen}
            closeFunc={() => {
              setWillCarShowModalOpen(false);
            }}
            width={getExOrInModalWidth()}
            padding={getResponsiveSize(35, 35)}
            otherStyle={{ color: '#05141f' }}
          >
            <div
              className={getFontClassName('medium', language)}
              style={{
                textAlign: 'center',
                fontSize: getResponsiveSize(23, 22),
                marginTop: 0,
                marginBottom: getResponsiveSize(44, 44),
                direction: getLanguageInfo(language).rtl ? 'rtl' : 'ltr',
              }}
            >
              {localization['pop_up_txt_0'][language]}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button
                onClick={() => {
                  if (carId !== null)
                    history.push({ pathname: `${createPrefixOfUrl()}/${carId}/exterior`, state: { color: carColor } });
                }}
                id="askUIExterior"
                className={`${getCarInfo(carId).carCode}_exterior`}
                style={{
                  border: `${isMobile ? 1 : 2}px solid #05141f`,
                  background: 'transparent',
                  color: '#05141F',
                  fontSize: getResponsiveSize(18, 18),
                  width: getResponsiveSize(165, 165),
                  height: getResponsiveSize(50, 50),
                  cursor: 'pointer',
                }}
              >
                {localization['explore_exterior'][language]}
              </button>
              <button
                onClick={() => {
                  if (carId !== null)
                    history.push({ pathname: `${createPrefixOfUrl()}/${carId}/interior`, state: { color: carColor } });
                }}
                id="askUIInterior"
                className={`${getCarInfo(carId).carCode}_interior`}
                style={{
                  border: `${isMobile ? 1 : 2}px solid #05141f`,
                  background: 'transparent',
                  color: '#05141F',
                  fontSize: getResponsiveSize(18, 18),
                  width: getResponsiveSize(165, 165),
                  height: getResponsiveSize(50, 50),
                  cursor: 'pointer',
                }}
              >
                {localization['explore_interior'][language]}
              </button>
            </div>
          </CustomModal>
          <ShowRoomMenu
            opened={fullMenuOpened}
            closeFunc={() => setOpenFullMenu(false)}
            languageInfo={getLanguageInfo(language)}
            setCarId={setCarId}
            setWillCarShowModalOpen={setWillCarShowModalOpen}
            lastCar={state === undefined ? cars[0].carCode : getCarInfo(state.current_car).carCode}
          />
          <GalleryMenu
            opened={galleryMenuOpened}
            closeFunc={() => setGalleryMenuOpened(false)}
            languageInfo={getLanguageInfo(language)}
            onCarClick={(idx) => {
              setCarId(idx);
              setCarGalleryOpened(true);
              gtmShowroomGalleryInteraction('Click|gallery open', getCarInfo(idx).brand);
            }}
          />
          {carGalleryOpened ? (
            <FullScreenImageCarousel carId={carId} closeFunc={() => setCarGalleryOpened(false)} fromShowroom={true} />
          ) : null}
        </PageWrapper>
      ) : (
        <div style={{ width: '100%', height: '100%', backgroundColor: '#05141f' }}></div>
      )}
    </>
  );
};

export default ShowRoom;
